import { Box, Flex, Text } from "@/components/display/display-primitives";
import {
  Wind,
  Droplets,
  Sunrise,
  Sunset,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  lookupWmo,
  type WeatherCategory,
  type WeatherSnapshot,
  type WeatherLayout,
  type WeatherStyle,
} from "@mem/shared";
import type { FeedItem } from "@/lib/display-feed";
import { pickBackground, pickMinimalIcon, pickWarmEmoji } from "@/lib/weather-icons";
import { useClockTime } from "@/hooks/use-clock-time";

interface WeatherSlideProps {
  item: FeedItem;
  isActive: boolean;
}

/**
 * Slide météo : 4 layouts (current / current_today / forecast_3day / clock),
 * 2 styles (warm: emojis colorés / minimal: icônes Lucide blanches).
 * Données fournies par le backend Go (cache 10min / fallback stale 6h).
 */
export function WeatherSlide({ item, isActive }: WeatherSlideProps) {
  const { t, i18n } = useTranslation();
  const data = item.data as {
    city_label?: string;
    country_code?: string;
    layout?: WeatherLayout;
    style?: WeatherStyle;
    snapshot?: WeatherSnapshot;
  };

  const city = data.city_label ?? "";
  const cc = data.country_code ?? "";
  const layout: WeatherLayout = data.layout ?? "current";
  const style: WeatherStyle = data.style ?? "warm";
  const snap = data.snapshot;

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        inset: 0,
        opacity: isActive ? 1 : 0,
        transition: "opacity 1s ease-in-out",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(2rem, 6vw, 5rem)",
        background: pickBackground(
          lookupWmo(snap?.current.wmo_code ?? 0).category,
          snap?.current.is_day ?? true,
        ),
        color: "rgba(255,255,255,0.95)",
      }}
    >
      {/* Header : ville + indicateur stale éventuel — masqué sur le layout clock
          (l'heure prend la place centrale, la ville est intégrée au bloc) */}
      {layout !== "clock" && (
        <Flex direction="column" align="center" gap="1" style={{ marginBottom: "clamp(1rem, 3vh, 2.5rem)" }}>
          <Text
            as="span"
            style={{
              fontSize: "clamp(1.4rem, 3.5vw, 2.4rem)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 500,
              opacity: 0.85,
            }}
          >
            {city}{cc ? ` · ${cc}` : ""}
          </Text>
          {snap?.stale && (
            <Text
              as="span"
              style={{
                fontSize: "clamp(0.75rem, 1.5vw, 1rem)",
                opacity: 0.6,
                fontStyle: "italic",
              }}
            >
              {t("weather.stale", { time: formatTime(snap.fetched_at, i18n.language) })}
            </Text>
          )}
        </Flex>
      )}

      {!snap && layout !== "clock" && (
        <Text style={{ opacity: 0.7 }}>{t("weather.unavailable")}</Text>
      )}

      {snap && layout === "current" && <CurrentBlock snap={snap} style={style} />}
      {snap && layout === "current_today" && <CurrentTodayBlock snap={snap} style={style} />}
      {snap && layout === "forecast_3day" && <Forecast3DayBlock snap={snap} style={style} />}
      {layout === "clock" && <ClockWeatherBlock snap={snap} style={style} city={city} cc={cc} />}
    </Box>
  );
}

// ─── Layout : Horloge + Météo ─────────────────────────────────────

/**
 * Layout "clock" : horloge en temps réel + météo courante.
 * Deux colonnes en paysage (tablette) — l'heure est dominante à gauche,
 * la météo synthétique à droite. Se réduit en pile sur petit écran.
 *
 * L'heure est mise à jour toutes les minutes via useClockTime().
 * Pas de dépendance réseau supplémentaire : l'heure vient du navigateur.
 */
function ClockWeatherBlock({
  snap,
  style,
  city,
  cc,
}: {
  snap: WeatherSnapshot | undefined;
  style: WeatherStyle;
  city: string;
  cc: string;
}) {
  const { t } = useTranslation();
  const clock = useClockTime();
  const wmo = lookupWmo(snap?.current.wmo_code ?? 0);

  return (
    <Flex
      align="center"
      justify="center"
      gap="0"
      wrap="wrap"
      style={{ width: "100%", maxWidth: 1200 }}
    >
      {/* ── Colonne gauche : horloge ──────────────────────── */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{ flex: "1 1 280px", padding: "clamp(1rem, 4vw, 3rem)" }}
      >
        <Text
          style={{
            fontSize: "clamp(5rem, 18vw, 14rem)",
            fontWeight: 100,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {clock.display}
        </Text>
        {/* Ville sous l'horloge */}
        <Text
          style={{
            fontSize: "clamp(0.9rem, 2vw, 1.4rem)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            opacity: 0.7,
            marginTop: "0.5rem",
          }}
        >
          {city}{cc ? ` · ${cc}` : ""}
        </Text>
      </Flex>

      {/* ── Séparateur vertical ───────────────────────────── */}
      <Box
        style={{
          width: 1,
          alignSelf: "stretch",
          minHeight: "10rem",
          background: "rgba(255,255,255,0.2)",
          flexShrink: 0,
          margin: "0 clamp(0.5rem, 2vw, 2rem)",
        }}
      />

      {/* ── Colonne droite : météo courante ──────────────── */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="3"
        style={{ flex: "1 1 200px", padding: "clamp(1rem, 4vw, 3rem)" }}
      >
        {snap ? (
          <>
            <WeatherIcon category={wmo.category} style={style} size="large" isDay={snap.current.is_day} />
            <Text style={{ fontSize: "clamp(3rem, 10vw, 7rem)", fontWeight: 200, lineHeight: 1 }}>
              {Math.round(snap.current.temperature_c)}°
            </Text>
            <Text style={{ fontSize: "clamp(1rem, 2.5vw, 1.6rem)", opacity: 0.85 }}>
              {t(wmo.key)}
            </Text>
            <CurrentMeta snap={snap} style={style} />
            {snap.stale && (
              <Text style={{ fontSize: "clamp(0.7rem, 1.2vw, 0.9rem)", opacity: 0.5, fontStyle: "italic" }}>
                {t("weather.stale_short")}
              </Text>
            )}
          </>
        ) : (
          <Text style={{ opacity: 0.6 }}>{t("weather.unavailable")}</Text>
        )}
      </Flex>
    </Flex>
  );
}

// ─── Layout : Météo courante ──────────────────────────────────────

function CurrentBlock({ snap, style }: { snap: WeatherSnapshot; style: WeatherStyle }) {
  const { t } = useTranslation();
  const wmo = lookupWmo(snap.current.wmo_code);
  return (
    <Flex direction="column" align="center" gap="4">
      <WeatherIcon category={wmo.category} style={style} size="huge" isDay={snap.current.is_day} />
      <Text style={{ fontSize: "clamp(4rem, 14vw, 11rem)", fontWeight: 200, lineHeight: 1 }}>
        {Math.round(snap.current.temperature_c)}°
      </Text>
      <Text style={{ fontSize: "clamp(1.2rem, 3vw, 2rem)", opacity: 0.85 }}>
        {t(wmo.key)}
      </Text>
      <Text style={{ fontSize: "clamp(0.9rem, 2vw, 1.4rem)", opacity: 0.65 }}>
        {t("weather.feels_like", { value: Math.round(snap.current.apparent_c) })}
      </Text>
      <CurrentMeta snap={snap} style={style} />
    </Flex>
  );
}

// ─── Layout : Météo + résumé du jour ─────────────────────────────

function CurrentTodayBlock({ snap, style }: { snap: WeatherSnapshot; style: WeatherStyle }) {
  const { t } = useTranslation();
  const wmo = lookupWmo(snap.current.wmo_code);
  return (
    <Flex direction="column" align="center" gap="3">
      <Flex align="center" gap="5" wrap="wrap" justify="center">
        <WeatherIcon category={wmo.category} style={style} size="large" isDay={snap.current.is_day} />
        <Text style={{ fontSize: "clamp(3.5rem, 11vw, 8rem)", fontWeight: 200, lineHeight: 1 }}>
          {Math.round(snap.current.temperature_c)}°
        </Text>
      </Flex>
      <Text style={{ fontSize: "clamp(1.1rem, 2.6vw, 1.7rem)", opacity: 0.85 }}>
        {t(wmo.key)}
      </Text>
      {snap.today && (
        <Flex align="center" gap="4" style={{ marginTop: "clamp(0.5rem, 2vh, 1.5rem)" }}>
          <MetaPair label={t("weather.min")} value={`${Math.round(snap.today.min_c)}°`} />
          <Divider />
          <MetaPair label={t("weather.max")} value={`${Math.round(snap.today.max_c)}°`} />
          <Divider />
          <MetaPair
            label={t("weather.sunrise")}
            value={formatHHMM(snap.today.sunrise)}
            icon={style === "warm" ? "🌅" : <Sunrise size={16} />}
          />
          <Divider />
          <MetaPair
            label={t("weather.sunset")}
            value={formatHHMM(snap.today.sunset)}
            icon={style === "warm" ? "🌇" : <Sunset size={16} />}
          />
        </Flex>
      )}
      <CurrentMeta snap={snap} style={style} />
    </Flex>
  );
}

// ─── Layout : Prévisions 3 jours ─────────────────────────────────

/**
 * Carte "Maintenant" mise en avant :
 * - Utilise la température LIVE (snap.current) au lieu du min/max journalier
 * - Fond et taille légèrement supérieurs aux cartes J+1 et J+2
 * - Affiche le ressenti et l'icône WMO en temps réel
 */
function Forecast3DayBlock({ snap, style }: { snap: WeatherSnapshot; style: WeatherStyle }) {
  const { t, i18n } = useTranslation();
  const days = snap.forecast ?? [];
  const currentWmo = lookupWmo(snap.current.wmo_code);

  return (
    <Flex direction="column" align="center" gap="4" style={{ width: "100%" }}>
      <Flex
        align="stretch"
        justify="center"
        gap="4"
        wrap="wrap"
        style={{ width: "100%", maxWidth: "1200px" }}
      >
        {/* ── Carte "Maintenant" (prominente) ──────────── */}
        <Flex
          direction="column"
          align="center"
          gap="2"
          style={{
            flex: "1.4 1 260px",
            padding: "clamp(1.2rem, 3.5vw, 2.5rem)",
            borderRadius: "1.2rem",
            background: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.22)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          }}
        >
          <Text
            style={{
              fontSize: "clamp(1rem, 2.2vw, 1.5rem)",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              opacity: 0.9,
              fontWeight: 600,
            }}
          >
            {t("weather.now")}
          </Text>
          <WeatherIcon category={currentWmo.category} style={style} size="large" isDay={snap.current.is_day} />
          <Text style={{ fontSize: "clamp(2.8rem, 8vw, 5rem)", fontWeight: 200, lineHeight: 1 }}>
            {Math.round(snap.current.temperature_c)}°
          </Text>
          <Text style={{ fontSize: "clamp(0.9rem, 2vw, 1.3rem)", opacity: 0.9 }}>
            {t(currentWmo.key)}
          </Text>
          {/* Ressenti + min/max du jour */}
          <Flex direction="column" align="center" gap="1" style={{ marginTop: "0.5rem" }}>
            <Text style={{ fontSize: "clamp(0.8rem, 1.6vw, 1.05rem)", opacity: 0.7 }}>
              {t("weather.feels_like", { value: Math.round(snap.current.apparent_c) })}
            </Text>
            {days[0] && (
              <Flex gap="2" align="baseline">
                <Text style={{ fontSize: "clamp(0.85rem, 1.8vw, 1.1rem)", opacity: 0.75 }}>
                  {Math.round(days[0].max_c)}°
                </Text>
                <Text style={{ fontSize: "clamp(0.75rem, 1.5vw, 0.95rem)", opacity: 0.5 }}>
                  / {Math.round(days[0].min_c)}°
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        {/* ── Cartes J+1 et J+2 (secondaires) ─────────── */}
        {days.slice(1, 3).map((d) => {
          const wmo = lookupWmo(d.wmo_code);
          return (
            <Flex
              key={d.date}
              direction="column"
              align="center"
              gap="2"
              style={{
                flex: "1 1 200px",
                padding: "clamp(1rem, 2.5vw, 1.8rem)",
                borderRadius: "1rem",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <Text
                style={{
                  fontSize: "clamp(0.9rem, 1.8vw, 1.2rem)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  opacity: 0.75,
                }}
              >
                {formatDay(d.date, i18n.language)}
              </Text>
              <WeatherIcon category={wmo.category} style={style} size="medium" isDay />
              <Text style={{ fontSize: "clamp(0.85rem, 1.6vw, 1.1rem)", opacity: 0.8 }}>
                {t(wmo.key)}
              </Text>
              <Flex gap="3" align="baseline">
                <Text style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.2rem)", fontWeight: 300 }}>
                  {Math.round(d.max_c)}°
                </Text>
                <Text style={{ fontSize: "clamp(0.9rem, 2vw, 1.3rem)", opacity: 0.55 }}>
                  {Math.round(d.min_c)}°
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}

// ─── Infos courantes (vent + humidité) ───────────────────────────

function CurrentMeta({ snap, style }: { snap: WeatherSnapshot; style: WeatherStyle }) {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      gap="5"
      style={{ marginTop: "clamp(0.5rem, 2vh, 1.5rem)", opacity: 0.85 }}
    >
      <MetaPair
        label={t("weather.wind")}
        value={`${Math.round(snap.current.wind_kmh)} km/h`}
        icon={style === "warm" ? "💨" : <Wind size={16} />}
      />
      <Divider />
      <MetaPair
        label={t("weather.humidity")}
        value={`${snap.current.humidity_pct}%`}
        icon={style === "warm" ? "💧" : <Droplets size={16} />}
      />
    </Flex>
  );
}

// ─── Helpers visuels ─────────────────────────────────────────────

function MetaPair({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <Flex direction="column" align="center" gap="1">
      <Flex align="center" gap="1">
        {icon}
        <Text style={{ fontSize: "clamp(0.7rem, 1.4vw, 0.95rem)", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.65 }}>
          {label}
        </Text>
      </Flex>
      <Text style={{ fontSize: "clamp(1rem, 2.2vw, 1.4rem)", fontWeight: 400 }}>
        {value}
      </Text>
    </Flex>
  );
}

function Divider() {
  return <Box style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.18)" }} />;
}

function WeatherIcon({ category, style, size, isDay }: {
  category: WeatherCategory;
  style: WeatherStyle;
  size: "medium" | "large" | "huge";
  isDay: boolean;
}) {
  if (style === "warm") {
    return (
      <Text
        aria-hidden
        style={{
          fontSize:
            size === "huge" ? "clamp(6rem, 16vw, 12rem)"
            : size === "large" ? "clamp(4rem, 10vw, 7rem)"
            : "clamp(2.5rem, 6vw, 4rem)",
          lineHeight: 1,
          animation: "weather-icon-in 600ms ease-out",
        }}
      >
        {pickWarmEmoji(category, isDay)}
      </Text>
    );
  }
  const px = size === "huge" ? 160 : size === "large" ? 96 : 56;
  const Icon = pickMinimalIcon(category, isDay);
  return (
    <Box style={{ animation: "weather-icon-in 600ms ease-out" }}>
      <Icon size={px} strokeWidth={1.2} />
    </Box>
  );
}

// ─── Helpers date/heure ──────────────────────────────────────────

function formatHHMM(iso: string): string {
  if (!iso) return "—";
  const match = iso.match(/T(\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  return iso;
}

function formatTime(iso: string | Date, lang: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso);
  }
}

function formatDay(dateIso: string, lang: string): string {
  if (!dateIso) return "—";
  try {
    return new Date(dateIso).toLocaleDateString(lang, { weekday: "short" });
  } catch {
    return dateIso;
  }
}
